/**
 * AWS Cognito Configuration
 * 
 * These values come from your Cognito User Pool setup.
 * In production, they're loaded from environment variables.
 */

// Configuration from environment or defaults for development
export const cognitoConfig = {
  // Cognito User Pool settings
  region: import.meta.env.VITE_COGNITO_REGION || 'us-east-1',
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
  
  // Cognito Hosted UI domain (e.g., your-domain.auth.us-east-1.amazoncognito.com)
  domain: import.meta.env.VITE_COGNITO_DOMAIN || '',
  
  // Callback URLs
  callbackUrl: import.meta.env.VITE_COGNITO_CALLBACK_URL || `${window.location.origin}/callback`,
  signOutUrl: import.meta.env.VITE_COGNITO_SIGNOUT_URL || window.location.origin,
  
  // OAuth scopes
  scopes: ['openid', 'email', 'profile'],
};

/**
 * Check if Cognito is configured
 */
export function isCognitoConfigured() {
  return !!(cognitoConfig.userPoolId && cognitoConfig.clientId && cognitoConfig.domain);
}

/**
 * Build the Cognito Hosted UI login URL
 */
export function getLoginUrl() {
  const { domain, clientId, callbackUrl, scopes } = cognitoConfig;
  
  if (!isCognitoConfigured()) {
    console.warn('Cognito not configured');
    return null;
  }
  
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    scope: scopes.join(' '),
    redirect_uri: callbackUrl,
  });
  
  return `https://${domain}/login?${params.toString()}`;
}

/**
 * Build the Cognito Hosted UI logout URL
 */
export function getLogoutUrl() {
  const { domain, clientId, signOutUrl } = cognitoConfig;
  
  if (!isCognitoConfigured()) {
    return signOutUrl;
  }
  
  const params = new URLSearchParams({
    client_id: clientId,
    logout_uri: signOutUrl,
  });
  
  return `https://${domain}/logout?${params.toString()}`;
}

/**
 * Exchange authorization code for tokens
 */
export async function exchangeCodeForTokens(code) {
  const { domain, clientId, callbackUrl } = cognitoConfig;
  
  const tokenUrl = `https://${domain}/oauth2/token`;
  
  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code: code,
    redirect_uri: callbackUrl,
  });
  
  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error_description || 'Failed to exchange code for tokens');
  }
  
  return response.json();
}

/**
 * Refresh the access token using refresh token
 */
export async function refreshTokens(refreshToken) {
  const { domain, clientId } = cognitoConfig;
  
  const tokenUrl = `https://${domain}/oauth2/token`;
  
  const params = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: refreshToken,
  });
  
  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });
  
  if (!response.ok) {
    throw new Error('Failed to refresh tokens');
  }
  
  return response.json();
}

/**
 * Parse JWT token payload (without verification - that's done server-side)
 */
export function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}
